// AuthProvider.tsx — provider React 19 com estado de autenticação.
//
// Wrappa Amplify v6 Auth APIs:
//   - getCurrentUser() para hidratar sessão na mount
//   - signOut() para logout
//   - Hub.listen('auth') para reagir a sign-in/sign-out de outros tabs
//
// Em dev/preview sem Amplify configurado, fornece sessão dummy
// (configurable via VITE_DEV_MOCK_USER=true no main.tsx).

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { getCurrentUser, signOut as amplifySignOut, fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import {
  AuthContext,
  type AuthContextValue,
  type AuthStatus,
  type AuthUser,
} from '@/lib/auth/AuthContext';
import { isAmplifyConfigured } from '@/lib/auth/configureAmplify';

interface AuthProviderProps {
  children: ReactNode;
  /** Para tests / dev: força sessão sem chamar Amplify. */
  mockUser?: AuthUser | null;
}

export function AuthProvider({ children, mockUser }: AuthProviderProps) {
  const initialStatus: AuthStatus =
    mockUser === undefined ? 'loading' : mockUser ? 'authenticated' : 'unauthenticated';

  const [status, setStatus] = useState<AuthStatus>(initialStatus);
  const [user, setUser] = useState<AuthUser | null>(mockUser ?? null);

  const hydrate = async () => {
    if (mockUser !== undefined) {
      setUser(mockUser);
      setStatus(mockUser ? 'authenticated' : 'unauthenticated');
      return;
    }

    if (!isAmplifyConfigured()) {
      setUser(null);
      setStatus('unauthenticated');
      return;
    }

    try {
      const current = await getCurrentUser();

      // Lemos do ID token (não de fetchUserAttributes) porque SAML access tokens
      // não carregam o scope aws.cognito.signin.user.admin necessário pra GetUser API.
      // ID token contém todos os claims SAML mapeados (email, name, custom:idc_groups).
      const session = await fetchAuthSession();
      const claims = session.tokens?.idToken?.payload ?? {};

      // Grupos: prioriza claim do SAML IdC (custom:idc_groups) > cognito:groups
      const idcGroupsRaw = claims['custom:idc_groups'];
      const cognitoGroupsRaw = claims['cognito:groups'];
      const groupsSource = idcGroupsRaw ?? cognitoGroupsRaw ?? '';

      let groups: string[] = [];
      if (Array.isArray(groupsSource)) {
        groups = groupsSource.map(String);
      } else if (typeof groupsSource === 'string' && groupsSource) {
        // SAML pode mandar como JSON-stringified array ou CSV
        try {
          const parsed = JSON.parse(groupsSource);
          groups = Array.isArray(parsed) ? parsed.map(String) : [groupsSource];
        } catch {
          groups = groupsSource
            .split(',')
            .map((g) => g.trim())
            .filter(Boolean);
        }
      }

      setUser({
        username: current.username,
        email: (claims.email as string) ?? '',
        name: claims.name as string | undefined,
        picture: (claims.picture as string | undefined) ?? undefined,
        groups,
      });
      setStatus('authenticated');
    } catch (err) {
      console.warn('[AuthProvider] hydrate failed', err);
      setUser(null);
      setStatus('unauthenticated');
    }
  };

  useEffect(() => {
    void hydrate();

    if (!isAmplifyConfigured() || mockUser !== undefined) return;

    const unsub = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedIn' || payload.event === 'signedOut') {
        void hydrate();
      }
    });
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mockUser]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      refresh: hydrate,
      signOut: async () => {
        if (!isAmplifyConfigured()) {
          setUser(null);
          setStatus('unauthenticated');
          return;
        }
        await amplifySignOut();
        setUser(null);
        setStatus('unauthenticated');
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [status, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
